import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { useState } from 'react';
import { useSearchParams, useNavigate, Link } from 'react-router-dom';
import {
  Sparkles,
  Loader2,
  Check,
  Layers,
  Package,
  Palette,
  Image,
  CheckSquare,
  Square,
  ArrowRight,
  DollarSign,
  AlertTriangle,
  Star,
  History,
  ChevronDown,
  ChevronRight,
  ExternalLink,
} from 'lucide-react';
import clsx from 'clsx';
import {
  api,
  type BatchStrategy,
  type ProductStyle,
  type BatchVariant,
  type PODProductType,
  type AnalysisInfluence,
} from '../api/client';

const STRATEGIES: { value: BatchStrategy; label: string; description: string }[] = [
  {
    value: 'different_concepts_styles',
    label: 'Different Concepts + Styles',
    description: 'Maximum variety - each variant gets unique concept and art style',
  },
  {
    value: 'same_concept_different_styles',
    label: 'One Concept, Multiple Styles',
    description: 'Same design concept rendered in different art styles',
  },
  {
    value: 'different_concepts_same_style',
    label: 'Multiple Concepts, One Style',
    description: 'Different designs all in the same consistent style',
  },
  {
    value: 'matrix',
    label: 'Matrix (Concepts x Styles)',
    description: 'Cross-product of multiple concepts with multiple styles',
  },
];

const STYLE_OPTIONS: { value: ProductStyle; label: string }[] = [
  { value: 'minimalist', label: 'Minimalist' },
  { value: 'watercolor', label: 'Watercolor' },
  { value: 'vintage', label: 'Vintage' },
  { value: 'boho', label: 'Boho' },
  { value: 'modern', label: 'Modern' },
  { value: 'whimsical', label: 'Whimsical' },
  { value: 'botanical', label: 'Botanical' },
  { value: 'geometric', label: 'Geometric' },
  { value: 'hand-drawn', label: 'Hand-drawn' },
  { value: 'abstract', label: 'Abstract' },
];

const POD_PRODUCT_TYPES: { value: PODProductType; label: string }[] = [
  { value: 'all', label: 'All Products' },
  { value: 't-shirt', label: 'T-Shirts' },
  { value: 'hoodie', label: 'Hoodies' },
  { value: 'mug', label: 'Mugs' },
  { value: 'poster', label: 'Posters' },
  { value: 'canvas', label: 'Canvas' },
  { value: 'tote-bag', label: 'Tote Bags' },
  { value: 'sticker', label: 'Stickers' },
];

// Batch History Card Component
type BatchHistoryCardProps = {
  batch: {
    id: string;
    niche_keyword: string;
    product_type: string;
    variant_count: number;
    variants_completed: number;
    variants_selected: number;
    variants_converted: number;
    status: string;
    total_cost_usd: number;
    created_at: string;
  };
  isExpanded: boolean;
  onToggleExpand: () => void;
  onLoad: () => void;
  formatDate: (date: string) => string;
  getBatchStatusBadge: (status: string) => string;
};

function BatchHistoryCard({
  batch,
  isExpanded,
  onToggleExpand,
  onLoad,
  formatDate,
  getBatchStatusBadge,
}: BatchHistoryCardProps) {
  // Fetch variants when expanded
  const { data: batchDetails } = useQuery({
    queryKey: ['batch', batch.id],
    queryFn: () => api.batch.get(batch.id),
    enabled: isExpanded,
  });

  const variants = batchDetails?.variants ?? [];

  return (
    <div className="border rounded-lg overflow-hidden">
      {/* Header */}
      <div
        className="p-4 bg-gray-50 flex items-center gap-4 cursor-pointer hover:bg-gray-100"
        onClick={onToggleExpand}
      >
        <button className="text-gray-500">
          {isExpanded ? <ChevronDown className="w-5 h-5" /> : <ChevronRight className="w-5 h-5" />}
        </button>
        <div className="flex-1 min-w-0">
          <div className="flex items-center gap-2">
            <span className="font-medium truncate">{batch.niche_keyword}</span>
            <span
              className={clsx(
                'px-2 py-0.5 rounded text-xs font-medium',
                batch.product_type === 'all'
                  ? 'bg-gray-100 text-gray-600'
                  : ['t-shirt', 'hoodie', 'tote-bag'].includes(batch.product_type)
                    ? 'bg-purple-100 text-purple-700'
                    : 'bg-blue-100 text-blue-700'
              )}
            >
              {POD_PRODUCT_TYPES.find((p) => p.value === batch.product_type)?.label ||
                batch.product_type}
            </span>
            <span
              className={clsx(
                'px-2 py-0.5 rounded text-xs font-medium',
                getBatchStatusBadge(batch.status)
              )}
            >
              {batch.status}
            </span>
          </div>
          <div className="text-sm text-gray-500 mt-0.5">
            {batch.variants_completed} variants • {batch.variants_converted} converted • $
            {batch.total_cost_usd.toFixed(3)}
          </div>
        </div>
        <div className="text-sm text-gray-500">{formatDate(batch.created_at)}</div>
        <button
          onClick={(e) => {
            e.stopPropagation();
            onLoad();
          }}
          className="px-3 py-1.5 text-sm font-medium text-etsy-orange hover:bg-etsy-orange/10 rounded-lg transition-colors"
        >
          View Batch
        </button>
      </div>

      {/* Expanded Variants */}
      {isExpanded && (
        <div className="border-t p-4 space-y-2 bg-white">
          {variants.length === 0 ? (
            <div className="flex items-center justify-center py-4 text-gray-500">
              <Loader2 className="w-5 h-5 animate-spin mr-2" />
              Loading variants...
            </div>
          ) : (
            variants.map((variant) => (
              <div
                key={variant.id}
                className="flex items-center gap-3 p-2 rounded-lg hover:bg-gray-50"
              >
                {variant.preview_image_url ? (
                  <img
                    src={variant.preview_image_url}
                    alt={variant.concept_name}
                    className="w-10 h-10 rounded object-cover"
                  />
                ) : (
                  <div className="w-10 h-10 rounded bg-gray-100 flex items-center justify-center">
                    <Image className="w-5 h-5 text-gray-400" />
                  </div>
                )}
                <div className="flex-1 min-w-0">
                  <div className="text-sm font-medium truncate">{variant.concept_name}</div>
                  <div className="text-xs text-gray-500">{variant.style}</div>
                </div>
                <span
                  className={clsx(
                    'px-2 py-0.5 rounded text-xs',
                    variant.status === 'converted'
                      ? 'bg-etsy-orange/20 text-etsy-orange'
                      : variant.status === 'selected'
                        ? 'bg-purple-100 text-purple-700'
                        : variant.status === 'rejected'
                          ? 'bg-red-100 text-red-700'
                          : 'bg-gray-100 text-gray-700'
                  )}
                >
                  {variant.status}
                </span>
                {variant.listing_id && (
                  <Link
                    to={`/listing/${variant.listing_id}`}
                    className="flex items-center gap-1 text-sm text-etsy-orange hover:underline"
                    onClick={(e) => e.stopPropagation()}
                  >
                    <ExternalLink className="w-4 h-4" />
                    View Listing
                  </Link>
                )}
              </div>
            ))
          )}
        </div>
      )}
    </div>
  );
}

export default function BatchGeneration() {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const queryClient = useQueryClient();

  // Get niche from URL params (supports both 'niche' and 'niche_id')
  const nicheIdParam = searchParams.get('niche') || searchParams.get('niche_id');
  const nicheId = nicheIdParam ? parseInt(nicheIdParam) : null;

  // Configuration state
  const [variantCount, setVariantCount] = useState(5);
  const [strategy, setStrategy] = useState<BatchStrategy>('different_concepts_styles');
  const [selectedStyles, setSelectedStyles] = useState<ProductStyle[]>([]);
  const [productType, setProductType] = useState<PODProductType>('all');
  const [influence, setInfluence] = useState<AnalysisInfluence>({
    targetAudience: 0.8,
    opportunities: 0.7,
    demandSignals: 0.6,
    priceRange: 0.5,
  });

  // Current batch state
  const [currentBatchId, setCurrentBatchId] = useState<string | null>(null);
  const [selectedVariantIds, setSelectedVariantIds] = useState<Set<string>>(new Set());

  // History state
  const [expandedBatchIds, setExpandedBatchIds] = useState<Set<string>>(new Set());

  // Fetch niches for selection
  const { data: nichesData } = useQuery({
    queryKey: ['niches'],
    queryFn: () => api.niches.list(),
  });

  // Fetch specific niche if ID provided
  const { data: selectedNicheData } = useQuery({
    queryKey: ['niche', nicheId],
    queryFn: () => (nicheId ? api.niches.get(nicheId) : Promise.resolve(null)),
    enabled: !!nicheId,
  });

  // Fetch current batch data
  const { data: batchData, refetch: refetchBatch } = useQuery({
    queryKey: ['batch', currentBatchId],
    queryFn: () => (currentBatchId ? api.batch.get(currentBatchId) : Promise.resolve(null)),
    enabled: !!currentBatchId,
    refetchInterval: (query) => {
      const data = query.state.data;
      if (data && data.batch.status === 'generating') {
        return 2000; // Poll every 2s while generating
      }
      return false;
    },
  });

  // Cost estimate
  const { data: costEstimate } = useQuery({
    queryKey: ['batch-estimate', variantCount],
    queryFn: () => api.batch.estimate(variantCount),
  });

  // Batch history
  const { data: historyData } = useQuery({
    queryKey: ['batch-history'],
    queryFn: () => api.batch.list({ limit: 20 }),
  });

  // Generate mutation
  const generateMutation = useMutation({
    mutationFn: (data: Parameters<typeof api.batch.generate>[0]) => api.batch.generate(data),
    onSuccess: (result) => {
      setCurrentBatchId(result.batch_id);
      queryClient.invalidateQueries({ queryKey: ['batches'] });
    },
  });

  // Select mutation
  const selectMutation = useMutation({
    mutationFn: (data: { action: 'select' | 'deselect' | 'reject'; variantIds: string[] }) =>
      api.batch.select(currentBatchId!, { variant_ids: data.variantIds, action: data.action }),
    onSuccess: () => {
      refetchBatch();
    },
  });

  // Convert mutation
  const convertMutation = useMutation({
    mutationFn: (data: Parameters<typeof api.batch.convert>[1]) =>
      api.batch.convert(currentBatchId!, data),
    onSuccess: (result) => {
      alert(`Successfully converted ${result.converted_count} variants to listings!`);
      navigate('/inventory');
    },
  });

  const niches = nichesData?.niches ?? [];
  const selectedNiche = selectedNicheData?.niche || niches.find((n) => n.id === nicheId);
  const batch = batchData?.batch;
  const variants = batchData?.variants ?? [];

  const handleStyleToggle = (style: ProductStyle) => {
    setSelectedStyles((prev) =>
      prev.includes(style) ? prev.filter((s) => s !== style) : [...prev, style]
    );
  };

  const handleVariantToggle = (variantId: string) => {
    setSelectedVariantIds((prev) => {
      const next = new Set(prev);
      if (next.has(variantId)) {
        next.delete(variantId);
      } else {
        next.add(variantId);
      }
      return next;
    });
  };

  const handleSelectAll = () => {
    const readyVariants = variants.filter((v) => v.status === 'ready' || v.status === 'selected');
    setSelectedVariantIds(new Set(readyVariants.map((v) => v.id)));
  };

  const handleDeselectAll = () => {
    setSelectedVariantIds(new Set());
  };

  const handleGenerate = () => {
    if (!nicheId) {
      alert('Please select a niche first');
      return;
    }

    generateMutation.mutate({
      niche_id: nicheId,
      variant_count: variantCount,
      strategy,
      product_type: productType,
      styles: selectedStyles.length > 0 ? selectedStyles : undefined,
      analysis_influence: influence,
    });
  };

  const handleMarkSelected = () => {
    if (selectedVariantIds.size === 0) return;
    selectMutation.mutate({ action: 'select', variantIds: Array.from(selectedVariantIds) });
  };

  const handleConvert = () => {
    const selectedVariants = variants.filter((v) => v.status === 'selected');
    if (selectedVariants.length === 0) {
      alert('Please select some variants first');
      return;
    }

    convertMutation.mutate({
      generate_seo: true,
      auto_stage: true,
    });
  };

  const getStatusBadge = (status: BatchVariant['status']) => {
    const styles: Record<string, string> = {
      pending: 'bg-gray-100 text-gray-700',
      generating: 'bg-blue-100 text-blue-700',
      ready: 'bg-green-100 text-green-700',
      selected: 'bg-purple-100 text-purple-700',
      converted: 'bg-etsy-orange/20 text-etsy-orange',
      rejected: 'bg-red-100 text-red-700',
      failed: 'bg-red-100 text-red-700',
    };
    return styles[status] || 'bg-gray-100 text-gray-700';
  };

  // Get AI recommended product type from niche analysis
  const getAIRecommendation = () => {
    if (!selectedNiche?.analysis) return null;
    try {
      const parsed = JSON.parse(selectedNiche.analysis);
      const analysis = parsed.analysis || parsed;
      const recommendations = analysis?.recommendedProductTypes;
      if (!recommendations?.length) return null;
      // Return the first (top) recommendation
      return recommendations[0];
    } catch {
      return null;
    }
  };

  const aiRecommendation = getAIRecommendation();

  // Batch history helpers
  const batchHistory = historyData?.batches ?? [];

  const toggleBatchExpand = (batchId: string) => {
    setExpandedBatchIds((prev) => {
      const next = new Set(prev);
      if (next.has(batchId)) {
        next.delete(batchId);
      } else {
        next.add(batchId);
      }
      return next;
    });
  };

  const loadBatch = (batchId: string) => {
    setCurrentBatchId(batchId);
    setSelectedVariantIds(new Set());
  };

  const formatDate = (dateStr: string) => {
    const date = new Date(dateStr);
    return date.toLocaleDateString('en-US', {
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
    });
  };

  const getBatchStatusBadge = (status: string) => {
    const styles: Record<string, string> = {
      pending: 'bg-gray-100 text-gray-700',
      generating: 'bg-blue-100 text-blue-700',
      ready: 'bg-green-100 text-green-700',
      partial: 'bg-yellow-100 text-yellow-700',
      failed: 'bg-red-100 text-red-700',
    };
    return styles[status] || 'bg-gray-100 text-gray-700';
  };

  // Show config or batch view based on state
  const showBatchView = currentBatchId && batch;

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold text-gray-900 flex items-center gap-2">
            <Layers className="w-7 h-7 text-etsy-orange" />
            Batch Variant Generation
          </h1>
          <p className="text-gray-600 mt-1">
            Generate multiple product variants from niche research with different styles and
            concepts
          </p>
        </div>
        {showBatchView && (
          <button
            onClick={() => {
              setCurrentBatchId(null);
              setSelectedVariantIds(new Set());
            }}
            className="px-4 py-2 text-gray-600 hover:text-gray-900 flex items-center gap-2"
          >
            <ArrowRight className="w-4 h-4 rotate-180" />
            New Batch
          </button>
        )}
      </div>

      {!showBatchView ? (
        /* Configuration View */
        <>
          <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
            {/* Left: Configuration */}
            <div className="lg:col-span-2 space-y-6">
              {/* Niche Selection */}
              <div className="bg-white rounded-xl shadow-sm border p-6">
                <h2 className="text-lg font-semibold mb-4 flex items-center gap-2">
                  <Package className="w-5 h-5" />
                  Select Niche
                </h2>
                <select
                  value={nicheId || ''}
                  onChange={(e) => {
                    const value = e.target.value;
                    if (value) {
                      navigate(`/batch?niche=${value}`);
                    }
                  }}
                  className="w-full px-4 py-2 border rounded-lg focus:ring-2 focus:ring-etsy-orange focus:border-etsy-orange"
                >
                  <option value="">Select a niche...</option>
                  {niches
                    .filter((n) => n.status === 'researched')
                    .map((niche) => (
                      <option key={niche.id} value={niche.id}>
                        {niche.keyword} (Score: {(niche.trend_score * 100).toFixed(0)}%)
                      </option>
                    ))}
                </select>
                {selectedNiche && (
                  <div className="mt-3 p-3 bg-gray-50 rounded-lg">
                    <div className="font-medium">{selectedNiche.keyword}</div>
                    <div className="text-sm text-gray-600">
                      Trend Score: {(selectedNiche.trend_score * 100).toFixed(0)}%
                    </div>
                  </div>
                )}
              </div>

              {/* Strategy Selection */}
              <div className="bg-white rounded-xl shadow-sm border p-6">
                <h2 className="text-lg font-semibold mb-4">Variant Strategy</h2>
                <div className="space-y-3">
                  {STRATEGIES.map((s) => (
                    <label
                      key={s.value}
                      className={clsx(
                        'flex items-start gap-3 p-3 rounded-lg border cursor-pointer transition-colors',
                        strategy === s.value
                          ? 'border-etsy-orange bg-etsy-orange/5'
                          : 'border-gray-200 hover:border-gray-300'
                      )}
                    >
                      <input
                        type="radio"
                        name="strategy"
                        value={s.value}
                        checked={strategy === s.value}
                        onChange={(e) => setStrategy(e.target.value as BatchStrategy)}
                        className="mt-1"
                      />
                      <div>
                        <div className="font-medium">{s.label}</div>
                        <div className="text-sm text-gray-600">{s.description}</div>
                      </div>
                    </label>
                  ))}
                </div>
              </div>

              {/* Style Selection */}
              <div className="bg-white rounded-xl shadow-sm border p-6">
                <h2 className="text-lg font-semibold mb-4 flex items-center gap-2">
                  <Palette className="w-5 h-5" />
                  Art Styles
                  <span className="text-sm font-normal text-gray-500">
                    (leave empty for all styles)
                  </span>
                </h2>
                <div className="flex flex-wrap gap-2">
                  {STYLE_OPTIONS.map((style) => (
                    <button
                      key={style.value}
                      onClick={() => handleStyleToggle(style.value)}
                      className={clsx(
                        'px-3 py-1.5 rounded-full text-sm font-medium transition-colors',
                        selectedStyles.includes(style.value)
                          ? 'bg-etsy-orange text-white'
                          : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
                      )}
                    >
                      {style.label}
                    </button>
                  ))}
                </div>
              </div>

              {/* Analysis Influence */}
              <div className="bg-white rounded-xl shadow-sm border p-6">
                <h2 className="text-lg font-semibold mb-4">Analysis Influence</h2>
                <p className="text-sm text-gray-600 mb-4">
                  Control how much the niche research data influences variant generation
                </p>
                <div className="space-y-4">
                  {[
                    { key: 'targetAudience', label: 'Target Audience' },
                    { key: 'opportunities', label: 'Opportunities' },
                    { key: 'demandSignals', label: 'Demand Signals' },
                    { key: 'priceRange', label: 'Price Range' },
                  ].map(({ key, label }) => (
                    <div key={key} className="flex items-center gap-4">
                      <label className="w-32 text-sm font-medium">{label}</label>
                      <input
                        type="range"
                        min="0"
                        max="1"
                        step="0.1"
                        value={influence[key as keyof AnalysisInfluence]}
                        onChange={(e) =>
                          setInfluence((prev) => ({
                            ...prev,
                            [key]: parseFloat(e.target.value),
                          }))
                        }
                        className="flex-1"
                      />
                      <span className="w-12 text-sm text-gray-600">
                        {(influence[key as keyof AnalysisInfluence] * 100).toFixed(0)}%
                      </span>
                    </div>
                  ))}
                </div>
              </div>
            </div>

            {/* Right: Summary & Action */}
            <div className="space-y-6">
              <div className="bg-white rounded-xl shadow-sm border p-6 sticky top-6">
                <h2 className="text-lg font-semibold mb-4">Generation Summary</h2>

                {/* Variant Count */}
                <div className="mb-6">
                  <label className="block text-sm font-medium mb-2">Number of Variants</label>
                  <div className="flex items-center gap-4">
                    <input
                      type="range"
                      min="1"
                      max="10"
                      value={variantCount}
                      onChange={(e) => setVariantCount(parseInt(e.target.value))}
                      className="flex-1"
                    />
                    <span className="w-8 text-center font-semibold">{variantCount}</span>
                  </div>
                </div>

                {/* Product Type */}
                <div className="mb-6">
                  <label className="block text-sm font-medium mb-2">Product Type</label>
                  <select
                    value={productType}
                    onChange={(e) => setProductType(e.target.value as PODProductType)}
                    className="w-full px-3 py-2 border rounded-lg"
                  >
                    {POD_PRODUCT_TYPES.map((pt) => (
                      <option key={pt.value} value={pt.value}>
                        {pt.label}
                      </option>
                    ))}
                  </select>
                  {/* AI Recommendation */}
                  {aiRecommendation && (
                    <div className="mt-2 p-2 bg-purple-50 border border-purple-100 rounded-lg">
                      <div className="flex items-center gap-2 text-sm">
                        <Star className="w-4 h-4 text-purple-600" />
                        <span className="text-purple-700">
                          <span className="font-medium">AI recommends:</span>{' '}
                          {aiRecommendation.type}
                        </span>
                      </div>
                      <div className="flex items-center gap-2 mt-1 text-xs text-purple-600">
                        <span
                          className={clsx(
                            'px-1.5 py-0.5 rounded',
                            aiRecommendation.difficulty === 'easy'
                              ? 'bg-green-100 text-green-700'
                              : aiRecommendation.difficulty === 'medium'
                                ? 'bg-yellow-100 text-yellow-700'
                                : 'bg-red-100 text-red-700'
                          )}
                        >
                          {aiRecommendation.difficulty}
                        </span>
                        <span
                          className={clsx(
                            aiRecommendation.profitMargin === 'high'
                              ? 'text-green-600'
                              : aiRecommendation.profitMargin === 'medium'
                                ? 'text-yellow-600'
                                : 'text-red-600'
                          )}
                        >
                          {aiRecommendation.profitMargin} margin
                        </span>
                      </div>
                    </div>
                  )}
                </div>

                {/* Cost Estimate */}
                {costEstimate && (
                  <div className="mb-6 p-3 bg-gray-50 rounded-lg">
                    <div className="flex items-center gap-2 text-sm font-medium mb-2">
                      <DollarSign className="w-4 h-4" />
                      Estimated Cost
                    </div>
                    <div className="text-2xl font-bold text-etsy-orange">
                      ${costEstimate.totalCost.toFixed(3)}
                    </div>
                    <div className="text-xs text-gray-500 mt-1">
                      Text: ${costEstimate.textCost.toFixed(3)} + Images: $
                      {costEstimate.imageCost.toFixed(3)}
                    </div>
                  </div>
                )}

                {/* Summary */}
                <div className="space-y-2 text-sm mb-6">
                  <div className="flex justify-between">
                    <span className="text-gray-600">Niche:</span>
                    <span className="font-medium">{selectedNiche?.keyword || 'Not selected'}</span>
                  </div>
                  <div className="flex justify-between">
                    <span className="text-gray-600">Strategy:</span>
                    <span className="font-medium">
                      {STRATEGIES.find((s) => s.value === strategy)?.label}
                    </span>
                  </div>
                  <div className="flex justify-between">
                    <span className="text-gray-600">Styles:</span>
                    <span className="font-medium">
                      {selectedStyles.length > 0 ? selectedStyles.length : 'All (10)'}
                    </span>
                  </div>
                  <div className="flex justify-between">
                    <span className="text-gray-600">Product:</span>
                    <span className="font-medium">
                      {POD_PRODUCT_TYPES.find((p) => p.value === productType)?.label}
                    </span>
                  </div>
                </div>

                {/* Generate Button */}
                <button
                  onClick={handleGenerate}
                  disabled={!nicheId || generateMutation.isPending}
                  className="w-full bg-etsy-orange text-white py-3 rounded-lg font-semibold hover:bg-etsy-orange/90 disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center gap-2"
                >
                  {generateMutation.isPending ? (
                    <>
                      <Loader2 className="w-5 h-5 animate-spin" />
                      Generating...
                    </>
                  ) : (
                    <>
                      <Sparkles className="w-5 h-5" />
                      Generate {variantCount} Variants
                    </>
                  )}
                </button>

                {generateMutation.isError && (
                  <div className="mt-3 p-3 bg-red-50 text-red-700 rounded-lg text-sm flex items-start gap-2">
                    <AlertTriangle className="w-4 h-4 mt-0.5" />
                    {generateMutation.error instanceof Error
                      ? generateMutation.error.message
                      : 'Generation failed'}
                  </div>
                )}
              </div>
            </div>
          </div>

          {/* Batch History Section */}
          {batchHistory.length > 0 && (
            <div className="bg-white rounded-xl shadow-sm border p-6">
              <h2 className="text-lg font-semibold mb-4 flex items-center gap-2">
                <History className="w-5 h-5" />
                Batch History
              </h2>
              <div className="space-y-3">
                {batchHistory.map((historyBatch) => (
                  <BatchHistoryCard
                    key={historyBatch.id}
                    batch={historyBatch}
                    isExpanded={expandedBatchIds.has(historyBatch.id)}
                    onToggleExpand={() => toggleBatchExpand(historyBatch.id)}
                    onLoad={() => loadBatch(historyBatch.id)}
                    formatDate={formatDate}
                    getBatchStatusBadge={getBatchStatusBadge}
                  />
                ))}
              </div>
            </div>
          )}
        </>
      ) : (
        /* Batch Preview View */
        <div className="space-y-6">
          {/* Batch Status */}
          <div className="bg-white rounded-xl shadow-sm border p-6">
            <div className="flex items-center justify-between mb-4">
              <div>
                <h2 className="text-lg font-semibold">{batch.niche_keyword}</h2>
                <p className="text-sm text-gray-600">
                  {batch.variants_completed} of {batch.variant_count} variants generated
                </p>
              </div>
              <div className="flex items-center gap-4">
                <span
                  className={clsx(
                    'px-3 py-1 rounded-full text-sm font-medium',
                    batch.status === 'ready'
                      ? 'bg-green-100 text-green-700'
                      : batch.status === 'generating'
                        ? 'bg-blue-100 text-blue-700'
                        : batch.status === 'partial'
                          ? 'bg-yellow-100 text-yellow-700'
                          : 'bg-gray-100 text-gray-700'
                  )}
                >
                  {batch.status}
                </span>
                <span className="text-sm text-gray-600">
                  Cost: ${batch.total_cost_usd.toFixed(3)}
                </span>
              </div>
            </div>

            {/* Selection Actions */}
            <div className="flex items-center gap-4 border-t pt-4">
              <button
                onClick={handleSelectAll}
                className="px-3 py-1.5 text-sm font-medium text-gray-700 hover:text-gray-900 flex items-center gap-1"
              >
                <CheckSquare className="w-4 h-4" />
                Select All
              </button>
              <button
                onClick={handleDeselectAll}
                className="px-3 py-1.5 text-sm font-medium text-gray-700 hover:text-gray-900 flex items-center gap-1"
              >
                <Square className="w-4 h-4" />
                Deselect All
              </button>
              <div className="flex-1" />
              <span className="text-sm text-gray-600">
                {selectedVariantIds.size} selected | {batch.variants_selected} marked for conversion
              </span>
              <button
                onClick={handleMarkSelected}
                disabled={selectedVariantIds.size === 0 || selectMutation.isPending}
                className="px-4 py-2 bg-purple-600 text-white rounded-lg text-sm font-medium hover:bg-purple-700 disabled:opacity-50 flex items-center gap-2"
              >
                <Check className="w-4 h-4" />
                Mark Selected
              </button>
              <button
                onClick={handleConvert}
                disabled={batch.variants_selected === 0 || convertMutation.isPending}
                className="px-4 py-2 bg-etsy-orange text-white rounded-lg text-sm font-medium hover:bg-etsy-orange/90 disabled:opacity-50 flex items-center gap-2"
              >
                {convertMutation.isPending ? (
                  <Loader2 className="w-4 h-4 animate-spin" />
                ) : (
                  <ArrowRight className="w-4 h-4" />
                )}
                Convert to Listings
              </button>
            </div>
          </div>

          {/* Variants Grid */}
          <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 xl:grid-cols-5 gap-4">
            {variants.map((variant) => (
              <div
                key={variant.id}
                onClick={() => {
                  if (variant.status === 'ready' || variant.status === 'selected') {
                    handleVariantToggle(variant.id);
                  }
                }}
                className={clsx(
                  'bg-white rounded-xl shadow-sm border overflow-hidden cursor-pointer transition-all',
                  selectedVariantIds.has(variant.id) && 'ring-2 ring-etsy-orange',
                  variant.status === 'converted' && 'opacity-60',
                  variant.status === 'rejected' && 'opacity-40'
                )}
              >
                {/* Preview Image */}
                <div className="aspect-square bg-gray-100 relative">
                  {variant.preview_image_url ? (
                    <img
                      src={variant.preview_image_url}
                      alt={variant.concept_name}
                      className="w-full h-full object-cover"
                    />
                  ) : variant.status === 'generating' ? (
                    <div className="absolute inset-0 flex items-center justify-center">
                      <Loader2 className="w-8 h-8 text-gray-400 animate-spin" />
                    </div>
                  ) : (
                    <div className="absolute inset-0 flex items-center justify-center">
                      <Image className="w-8 h-8 text-gray-400" />
                    </div>
                  )}

                  {/* Selection indicator */}
                  {(variant.status === 'ready' || variant.status === 'selected') && (
                    <div className="absolute top-2 right-2">
                      {selectedVariantIds.has(variant.id) ? (
                        <CheckSquare className="w-6 h-6 text-etsy-orange bg-white rounded" />
                      ) : (
                        <Square className="w-6 h-6 text-gray-400 bg-white rounded" />
                      )}
                    </div>
                  )}

                  {/* Status badge */}
                  <div className="absolute bottom-2 left-2">
                    <span
                      className={clsx(
                        'px-2 py-0.5 rounded text-xs font-medium',
                        getStatusBadge(variant.status)
                      )}
                    >
                      {variant.status}
                    </span>
                  </div>
                </div>

                {/* Info */}
                <div className="p-3">
                  <h3 className="font-medium text-sm truncate" title={variant.concept_name}>
                    {variant.concept_name}
                  </h3>
                  <div className="flex items-center gap-2 mt-1">
                    <span className="text-xs px-2 py-0.5 bg-gray-100 rounded">{variant.style}</span>
                  </div>
                  {variant.niche_analysis_influence && (
                    <div className="mt-2 text-xs text-gray-500 truncate">
                      {variant.niche_analysis_influence.targetAudience &&
                        `For: ${variant.niche_analysis_influence.targetAudience}`}
                    </div>
                  )}
                </div>
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  );
}
